import * as Dialog from '@radix-ui/react-dialog'
import { Form } from '@epages/react-components'
import type { TFunction, TOptions } from 'i18next'
import { upperFirst } from 'lodash-es'
import { useDispatch, useSelector } from 'react-redux'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import Immutable from 'immutable'
import cc from 'classcat'
import slugify from 'speakingurl'

import { PageCreatePanel } from '../Panels/PageCreatePanel'
import {
  activateEditorSidebarModule,
  createPage,
  listCategories,
  loadFooterPages,
  loadNavigation,
} from '../../../store/actions'
import { findDeep } from '../../../utils/immutableTreeHelpers'
import { generatePageUrl, withQueryAndScope } from '../../../urlGeneratorsPlain'
import { useTranslate, withComponents } from '../../../utils/translate'
import Button from '../../formComponents/Button'
import flatMap from '../../../utils/nestedListUtils/flatMap'

const keyPrefix = 'components.editorSidebarComponent.pageManagerSection'

type PageModalProps = {
  footerPages: ImmutableList
  mainNavigation: ImmutableList
  page: ImmutableMap | undefined
  mode: 'create' | 'edit' | 'duplicate'
  onRequestClose: () => void
}

export function CreatePageModal({ footerPages, mainNavigation, page, mode, onRequestClose }: Readonly<PageModalProps>) {
  const t = useTranslate('interface')

  const lang = useSelector<State, string>((state) => state.getIn(['shop', 'locale'], '').substring(0, 2))
  const isBeyondShop = useSelector<State, boolean>((state) => Boolean(state.getIn(['shop', 'beyond'])))
  const mboBaseUrl = useSelector<State, string>((state) => state.getIn(['shop', 'mboBaseUrl']))
  const categories = useSelector<State, ImmutableList>((state) => state.get('categoriesList').toList())
  const dispatch = useDispatch<GlobalDispatch>()
  const location = useLocation()

  const [state, setState] = useState({
    isSubmitting: false,
    submitError: null,
  })

  const navigate = useNavigate()

  const slugifyOptions = { lang, truncate: 250 }

  const pageType = page?.get('type')

  const tModal = (subKey: string, options?: TOptions | undefined) => {
    if (page) {
      const keyPartForType = {
        category: 'categories',
        blank: 'contentPages',
        legal: 'legalPages',
      }[pageType]

      return t(`${keyPrefix}.${keyPartForType}Section.pageSettingsModal.${subKey}`, options)
    }

    return t(`${keyPrefix}.${'contentPagesSection.createPageModal'}.${subKey}`, options)
  }

  const dialogTitle = tModal(pageType === 'blank' ? `title.on${upperFirst(mode)}` : 'title')

  const pageSlugs = flatMap(
    mainNavigation.concat(footerPages),
    (entry) => entry.get('type') === 'blank' && entry.get('slug'),
  ).filter(Boolean)

  const renderError = ({ status, message }: { status: number; message: string }) => {
    const maximumPageCountReached = message === 'Maximum page count reached.'

    return (
      <div
        className={cc({
          'ep-form-row': true,
          'ep-alert-info': status >= 500,
          'ep-notification-warning': !(status && status >= 500),
        })}
      >
        {maximumPageCountReached ? (
          <div>
            {withComponents(tModal)('errorMessages.maximumPageCountReached', {
              updateLink: (
                <a href={`${mboBaseUrl}/help`} target="_parent">
                  {tModal('errorMessages.updateLink')}
                </a>
              ),
            })}
          </div>
        ) : (
          tModal('errorMessages.generic')
        )}
      </div>
    )
  }

  const validate = (data, isOnSubmit = true) => {
    const normalizedData = normalize(data)
    const title = normalizedData.has('title') && normalizedData.get('title')
    const slug = title ? slugify(title, slugifyOptions) : ''
    const titleEmptyValidationResult = title ? null : tModal('pageNameInputField.validationMessages.isRequired')

    const slugTooShortValidationResult =
      title && slug.length < 1 ? tModal('pageNameInputField.validationMessages.onlySpecialCharacters') : null

    const navigationError =
      normalizedData.get('navigation') === 'footer' &&
      page &&
      page.get('navigation') === 'main' &&
      page.get('children') &&
      page.get('children').size > 0 &&
      tModal('pageNameInputField.validationMessages.cannotMakeFooterPageWithChildren')

    return Immutable.fromJS({
      title: isOnSubmit ? titleEmptyValidationResult || slugTooShortValidationResult || navigationError || null : null,
    })
  }

  const normalize = (data) => {
    return data.update('title', (title) => title.trim())
  }

  const enforceUniqueSlug = (slug, uniquifier = 1) => {
    if (!pageSlugs.includes(slug)) return slug

    return pageSlugs.includes(`${slug}-${uniquifier}`)
      ? enforceUniqueSlug(slug, uniquifier + 1)
      : `${slug}-${uniquifier}`
  }

  // Auto-checks the "Add pages & content" to-do in the BYD cockpit's dashboard checklist and setup guide
  const completeAddPageTask = () => {
    window.parent?.postMessage(
      {
        type: 'CHECKLIST',
        payload: {
          websiteOnly: 'contentSection.addContent',
          regularShop: 'onlineShopSetup.addPages',
        },
      },
      mboBaseUrl,
    )
  }

  const handleSubmit = async (newPage) => {
    setState((prev) => ({ ...prev, isSubmitting: true }))

    const pageParentSlug = newPage.get('pageParentSlug', null)
    const parentPage = findDeep(mainNavigation, (node) => node.get('slug') === pageParentSlug)

    const payload = {
      pageSlug: enforceUniqueSlug(slugify(newPage.get('title'), slugifyOptions)),
      title: newPage.get('title'),
      pageType: 'blank',
      navigation: newPage.get('navigation'),
      isVisible: parentPage?.get('isVisible') === false ? false : newPage.get('isVisible'),
      collectionId: newPage.getIn(['selectedCollectionInfo', 'isActive'])
        ? newPage.getIn(['selectedCollectionInfo', 'collectionId'])
        : null,
      pageParentSlug,
    } as const

    try {
      const createdPage = (await dispatch(
        createPage(payload, { showErrorNotification: false }),
      )) as unknown as Core.Page

      navigate(withQueryAndScope(generatePageUrl(createdPage.type, createdPage.slug), location))
      if (!newPage.getIn(['selectedCollectionInfo', 'isActive']))
        dispatch(activateEditorSidebarModule('content-creation'))
      if (createdPage.navigation === 'main') await dispatch(loadNavigation())
      else if (createdPage.navigation === 'footer') await dispatch(loadFooterPages())
      setState((prev) => ({ ...prev, isSubmitting: false }))
      completeAddPageTask()
      onRequestClose()
    } catch (err) {
      setState((prev) => ({ ...prev, isSubmitting: false, submitError: err }))
    }
  }

  useEffect(() => {
    if (isBeyondShop && !categories.size) {
      dispatch(listCategories())
    }
  }, [dispatch, isBeyondShop, categories.size])

  return (
    <Dialog.Root open onOpenChange={onRequestClose} modal>
      <Dialog.Portal>
        <div className="ep-ui ep-modal-dialog">
          <Dialog.Overlay className="ep-modal-overlay" />
          <Dialog.Content asChild>
            <div className="ep-modal-content">
              <Form name="createPage" value={page} onSubmit={handleSubmit} validate={validate} normalize={normalize}>
                {({ value }) => (
                  <>
                    <div className="ep-modal-header">
                      <Dialog.Close className="ep-modal-close" />
                      <Dialog.Title asChild>
                        <div className="ep-modal-title">{dialogTitle}</div>
                      </Dialog.Title>
                      <Dialog.Description className="visually-hidden">Description for Foo</Dialog.Description>
                    </div>
                    <div className="ep-modal-body">
                      {state.submitError && renderError(state.submitError)}

                      <PageCreatePanel
                        changedPage={value}
                        tModal={tModal as TFunction}
                        isBeyondShop={isBeyondShop}
                        categories={categories}
                      />
                    </div>
                    <div className="ep-modal-footer">
                      <Button
                        type="button"
                        variant="cancel"
                        disabled={state.isSubmitting}
                        onClick={() => onRequestClose()}
                      >
                        {tModal('cancelButton.label')}
                      </Button>
                      <Button type="submit" disabled={state.isSubmitting}>
                        {tModal('saveButton.label')}
                      </Button>
                    </div>
                  </>
                )}
              </Form>
            </div>
          </Dialog.Content>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
